import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { env } from '~/lib/env';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import type { LabelListItem } from '~settings/api/lib/schemas';
import { LabelName } from '~settings/modules/label/components/LabelName';

import { ListLabelActions } from '../parts/ListLabelActions';
interface UseLabelsColumns {
  labelTypeFilter?: 'parent' | 'sublabel';
}

export const useLabelsColumns = ({
  labelTypeFilter,
}: UseLabelsColumns = {}) => {
  const isCurrentUserExternal = useIsExternalUser();
  const readOnlyIsNotEnabled =
    env('VITE_CONTAINER_SETTINGS_READ_ONLY_ENABLED', false) === 'false';
  const navigate = useNavigate();
  const { t } = useTranslation('settings');

  return useMemo(() => {
    const columnHelper = createColumnHelper<LabelListItem>();

    const sublabelsColumns = {
      name: columnHelper.accessor('name', {
        id: 'name',
        header: () => t('label.titles'),
        cell: (cell) => <LabelName name={cell.getValue()} />,
        enableSorting: true,
      }),
      labelType: columnHelper.accessor('isParentLabel', {
        id: 'type',
        header: () => t('labelType'),
        cell: (cell) => t(cell.getValue() ? 'parentLabel' : 'sublabel'),
        enableSorting: true,
      }),
      labelGroup: columnHelper.accessor((row) => row.labelGroups[0]?.name, {
        id: 'labelGroup',
        header: () => t('labelGroup.list'),
        cell: (cell) => (cell.getValue() ? cell.getValue() : '-'),
        enableSorting: false,
      }),
      usersCount: columnHelper.accessor('usersCount', {
        id: 'usersCount',
        header: () => t('users'),
        cell: (cell) => (cell.getValue() ? cell.getValue() : '-'),
        enableSorting: true,
      }),
      sublabelsCount: columnHelper.accessor('sublabelsCount', {
        id: 'sublabelsCount',
        header: () => t('sublabels'),
        cell: (cell) => (cell.getValue() ? cell.getValue() : '-'),
        enableSorting: true,
      }),
      controls: columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
          <ListLabelActions
            // TODO: replace with actual handler
            onDeleteClick={() => console.log(`delete ${row.original.id}`)}
            onEditClick={() =>
              navigate(SETTINGS_ROUTES.LABEL_EDIT(row.original.id))
            }
          />
        ),
      }),
    };

    let columnsOrder: Array<keyof typeof sublabelsColumns> = [];

    if (isCurrentUserExternal) {
      columnsOrder = ['name', 'labelType', 'usersCount', 'sublabelsCount'];
    } else {
      switch (labelTypeFilter) {
        case 'sublabel':
          columnsOrder = ['name', 'labelType', 'usersCount', 'sublabelsCount'];
          break;
        case 'parent':
        default:
          columnsOrder = [
            'name',
            'labelType',
            'labelGroup',
            'usersCount',
            'sublabelsCount',
          ];
          break;
      }
    }

    if (readOnlyIsNotEnabled) {
      columnsOrder.push('controls');
    }

    return columnsOrder.map((key) => sublabelsColumns[key]);
  }, [
    isCurrentUserExternal,
    labelTypeFilter,
    navigate,
    readOnlyIsNotEnabled,
    t,
  ]);
};
